import React from 'react';
import './SettingsRow.css';

/**
 * Small primitives used by Settings forms to keep field/value/toggle
 * layouts consistent. Built specifically for the Calendar/Settings/
 * Cancellation cards under Settings → Business, but reusable anywhere.
 *
 * Three pieces:
 *   <ToggleRow>           — label + on/off checkbox styled as a switch
 *   <NumberWithSuffix>    — number input with a glued unit chip ("min", "%")
 *   <SettingsField>       — vertical label + control with consistent spacing
 */

/* ------------------------------------------------------------- */
/*  ToggleRow                                                    */
/* ------------------------------------------------------------- */

interface ToggleRowProps {
    label: string;
    description?: string;
    checked: boolean;
    onChange: (next: boolean) => void;
    disabled?: boolean;
}

export const ToggleRow: React.FC<ToggleRowProps> = ({
    label,
    description,
    checked,
    onChange,
    disabled,
}) => (
    <label className={`gs-toggle-row ${disabled ? 'is-disabled' : ''}`} data-cy={`settings-row-toggle-${label.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}>
        {/* Square checkbox on the left so this row visually matches the
            other Settings checkboxes (e.g. Accepted Payment Methods). */}
        <input
            type="checkbox"
            className="gs-toggle-row__checkbox"
            checked={checked}
            disabled={disabled}
            onChange={(e) => onChange(e.target.checked)}
        />
        <span className="gs-toggle-row__text">
            <span className="gs-toggle-row__label">{label}</span>
            {description && (
                <span className="gs-toggle-row__desc">{description}</span>
            )}
        </span>
    </label>
);

/* ------------------------------------------------------------- */
/*  NumberWithSuffix                                             */
/* ------------------------------------------------------------- */

interface NumberWithSuffixProps {
    id?: string;
    value: number | string;
    onChange: (next: string) => void;
    suffix: string;
    placeholder?: string;
    min?: number;
    step?: number;
    /** Upper bound applied AT INPUT TIME (not just backend). E.g. a percentage
     *  field passes max={100} so "20222222" can never be typed. Required for any
     *  bounded field — without it the sanitizer only blocks letters, not range.
     *  IMPORTANT: max is in the UNIT THE FIELD DISPLAYS (meses, horas, minutos),
     *  which may differ from how the backend stores/validates it. */
    max?: number;
    /** Integer-only field (minutes, months, days, hours): decimal separators are
     *  rejected entirely — "1.090000" can never be typed into a minutes field. */
    integer?: boolean;
    disabled?: boolean;
}

export const NumberWithSuffix: React.FC<NumberWithSuffixProps> = ({
    id,
    value,
    onChange,
    suffix,
    placeholder,
    // min/step kept in the props API for compatibility; the sanitizer below is
    // what actually guards the input now.
    min = 0,
    step = 1,
    max,
    integer,
    disabled,
}) => (
    <div className={`gs-suffix-input ${disabled ? 'is-disabled' : ''}`}>
        {/* QA-STANDARDS §1.2: never a raw type="number" — it accepts 'e', '+',
            '-' and scientific notation ("1e10"). Text + inputMode + sanitizer
            allows only digits and one decimal dot; negatives are impossible by
            construction. When `max` is set the value is CLAMPED at input time so
            an out-of-range number (e.g. 20222222 in a 0–100 field) can't be
            typed. Backend re-validates the business ranges regardless. */}
        <input
            id={id}
            type="text"
            inputMode="decimal"
            maxLength={8}
            value={value ?? ''}
            placeholder={placeholder}
            disabled={disabled}
            onChange={(e) => {
                let clean = integer
                    // integer fields: digits ONLY — a decimal dot is as invalid as a letter
                    ? e.target.value.replace(/[^0-9]/g, '')
                    : e.target.value
                        .replace(/[^0-9.,]/g, '')
                        .replace(',', '.')
                        .replace(/^(\d*\.\d*).*$/, '$1'); // keep only the first dot
                // "00000002" is garbage: drop leading zeros (keeps "0" and "0.5").
                clean = clean.replace(/^0+(?=\d)/, '');
                // BLOCK (don't accept) anything above max: the invalid value never
                // appears and the field keeps its last valid content. This is NOT a
                // silent rewrite to max — a keystroke that would overflow is simply
                // rejected, like maxLength does for text. Backend still re-validates.
                if (max != null && clean !== '' && clean !== '.') {
                    const n = parseFloat(clean);
                    if (!Number.isNaN(n) && n > max) return; // reject this keystroke
                }
                onChange(clean);
            }}
        />
        <span className="gs-suffix-input__suffix">{suffix}</span>
    </div>
);

/* ------------------------------------------------------------- */
/*  SettingsField — label on top, control below, consistent gap. */
/* ------------------------------------------------------------- */

interface SettingsFieldProps {
    label: string;
    htmlFor?: string;
    description?: string;
    children: React.ReactNode;
    /** Optional Cypress selector forwarded to the field wrapper. */
    dataCy?: string;
}

export const SettingsField: React.FC<SettingsFieldProps> = ({
    label,
    htmlFor,
    description,
    children,
    dataCy,
}) => (
    <div className="gs-settings-field" data-cy={dataCy ?? 'settings-row'}>
        <label className="gs-settings-field__label" htmlFor={htmlFor}>
            {label}
        </label>
        {children}
        {description && (
            <span className="gs-settings-field__desc">{description}</span>
        )}
    </div>
);
